import { useMemo } from 'react';
import { BookOpen, Calendar, Trophy, Users } from 'lucide-react';
import {
  TierFeatureKey,
  buildOrgEntitlements,
  hasEntitlement,
} from '@court-sync/shared';
import type { WebsiteEmbedWidget } from '@/components/organization/WebsiteEmbedWidgets';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { useMatchPlayAccess } from '@/hooks/useMatchPlayAccess';
import { useSubscription } from '@/hooks/useSubscription';
import {
  buildOrgEmbedAbsoluteUrl,
  type OrgBrandedUrlContext,
  type OrgPathFields,
} from '@/lib/orgPaths';

export function useOrgWebsiteEmbedWidgets(publicOrg: OrgPathFields) {
  const { currentTier } = useSubscription();
  const { hasAcademyManagement, hasClubManagement, entitlements, isLoading: clubLoading } =
    useClubModuleAccess();
  const { hasAccess: hasMatchPlay, isLoading: matchPlayLoading } = useMatchPlayAccess();

  const brandedContext = useMemo<OrgBrandedUrlContext>(
    () => ({ tier: currentTier, clubTier: entitlements?.clubTier ?? null }),
    [currentTier, entitlements?.clubTier]
  );

  const orgEntitlements = useMemo(
    () =>
      entitlements ??
      buildOrgEntitlements({ clubManagement: hasClubManagement, tier: currentTier }),
    [entitlements, hasClubManagement, currentTier]
  );

  const widgets = useMemo(() => {
    const academyWidgets: WebsiteEmbedWidget[] = [];
    const clubWidgets: WebsiteEmbedWidget[] = [];

    if (hasAcademyManagement) {
      academyWidgets.push({
        id: 'lessons',
        title: 'Lesson browser',
        description: 'Browse group lessons, camps, and term programs on your public schedule.',
        embedUrl: buildOrgEmbedAbsoluteUrl(publicOrg, '/lessons', brandedContext),
        iframeTitle: 'Browse lessons',
        icon: BookOpen,
      });
    }

    if (hasClubManagement) {
      if (hasEntitlement(orgEntitlements, TierFeatureKey.COURT_HIRE)) {
        clubWidgets.push({
          id: 'court-hire',
          title: 'Court hire',
          description: 'Guest court booking with availability grid and checkout.',
          embedUrl: buildOrgEmbedAbsoluteUrl(publicOrg, '/court-hire', brandedContext),
          iframeTitle: 'Book a court',
          allowPayment: true,
          icon: Calendar,
        });
      }

      if (hasEntitlement(orgEntitlements, TierFeatureKey.MEMBERSHIPS)) {
        clubWidgets.push({
          id: 'memberships',
          title: 'Membership plans',
          description: 'Browse membership options and start online join checkout.',
          embedUrl: buildOrgEmbedAbsoluteUrl(publicOrg, '/join', brandedContext),
          iframeTitle: 'Join the club',
          allowPayment: true,
          icon: Users,
        });
      }

      if (hasMatchPlay) {
        clubWidgets.push({
          id: 'play-events',
          title: 'Play events',
          description: 'Social play, tournaments, and open match-play listings.',
          embedUrl: buildOrgEmbedAbsoluteUrl(publicOrg, '/play', brandedContext),
          iframeTitle: 'Play events',
          allowPayment: true,
          icon: Trophy,
        });
      }
    }

    return { academyWidgets, clubWidgets, allWidgets: [...academyWidgets, ...clubWidgets] };
  }, [
    brandedContext,
    hasAcademyManagement,
    hasClubManagement,
    hasMatchPlay,
    orgEntitlements,
    publicOrg,
  ]);

  return {
    ...widgets,
    isLoading: clubLoading || matchPlayLoading,
    brandedContext,
  };
}
